import {
  ChevronDownIcon,
  CalendarIcon,
  PrinterIcon,
  UserIcon,
  ReceiptTaxIcon,
} from "@heroicons/react/outline";

import { useEffect, useState, useRef } from "react";
import swal from "sweetalert";
import { selectCurrentUser } from "../../Features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingBar from "react-top-loading-bar";
import { useIsOnline } from "react-use-is-online";

import "./ManageReceiptsView.css";

import { motion, AnimatePresence } from "framer-motion";

import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { DateRangePicker } from "react-date-range";
import { addDays, subDays } from "date-fns";

import { useGetUserEmployeesQuery } from "../../Features/userEmployeesSlice";

import {
  useGetSalesByTaxNameByDateRangeMutation,
  useGetSalesByTaxNameQuery,
} from "../../Features/userReportsSlice";

import {
  selectDateRange,
  setDateRange,
} from "../../Features/date/dateSlice";

import { useTheme } from "../../ThemeContext";

function ManageTaxReportView({}) {
  const { darkMode } = useTheme();

  const user = useSelector(selectCurrentUser);
  const date_range = useSelector(selectDateRange);
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const { isOnline } = useIsOnline();

  const [currentItems, setCurrentItems] = useState([]);
  const [stats, setStats] = useState({});

  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(0);

  const pickerRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(false);

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: addDays(new Date(), 1),
      key: "selection",
    },
  ]);

  const initialStartDate = date_range
    ? format(date_range.startDate, "yyyy-MM-dd")
    : format(subDays(new Date(), 7), "yyyy-MM-dd");

  const initialEndDate = date_range
    ? format(date_range.endDate, "yyyy-MM-dd")
    : format(new Date(), "yyyy-MM-dd");

  // ---------------------------------------------------------
  // GET TAX REPORT
  // ---------------------------------------------------------

  const {
    data,
    isLoading,
    isSuccess,
  } = useGetSalesByTaxNameQuery({
    user_id: user.id,
    start_date: initialStartDate,
    end_date: initialEndDate,
    employee_id: "0",
  });

  // ---------------------------------------------------------
  // GET EMPLOYEES
  // ---------------------------------------------------------

  const { data: employees } = useGetUserEmployeesQuery({
    account_id: user.id,
    paginate: 10,
  });

  // ---------------------------------------------------------
  // DATE RANGE SEARCH MUTATION
  // ---------------------------------------------------------

  const [fetchCustomData] =
    useGetSalesByTaxNameByDateRangeMutation();

  // ---------------------------------------------------------
  // INITIAL DATA
  // ---------------------------------------------------------

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && data?.data) {
      setCurrentItems(data.data.details || []);
      setStats(data.data.summary || {});
    }

    setProgress(100);

    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [data, isLoading, isSuccess]);

  // ---------------------------------------------------------
  // FORMAT MONEY
  // ---------------------------------------------------------

  const formatMoney = (value) => {
    return Number(value ?? 0).toLocaleString("en-ZM", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ---------------------------------------------------------
  // EMPLOYEE CHANGE
  // ---------------------------------------------------------

  const handleEmployeeChange = (e, employeeId) => {
    submitSearch(e, employeeId);
  };

  // ---------------------------------------------------------
  // DATE RANGE SELECT
  // ---------------------------------------------------------

  const handleSelect = async (ranges) => {
    const { selection } = ranges;

    setState([selection]);

    // DateRangePicker fires twice:
    // first click = start date
    // second click = end date
    if (!isSelecting) {
      setIsSelecting(true);
      return;
    }

    setIsSelecting(false);
    setOpen(false);

    const startDate = format(
      selection.startDate,
      "yyyy-MM-dd"
    );

    const endDate = format(
      selection.endDate,
      "yyyy-MM-dd"
    );

    dispatch(
      setDateRange({
        date_range: selection,
      })
    );

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    try {
      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee
          ? selectedEmployee
          : "0",
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.details || []);
        setStats(resp.data.summary || {});
      } else {
        setCurrentItems([]);
        setStats({});
        swal("No results found.");
      }
    } catch (err) {
      setCurrentItems([]);
      setStats({});
      swal("Error", "Data not found", "error");
    } finally {
      setProgress(100);
    }
  };

  // ---------------------------------------------------------
  // SEARCH / EMPLOYEE FILTER
  // ---------------------------------------------------------

  const submitSearch = async (
    e,
    user_employee_id = false
  ) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const employee_id = user_employee_id
      ? user_employee_id
      : "0";

    try {
      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        start_date: selectedStartDate
          ? selectedStartDate
          : initialStartDate,
        end_date: selectedEndDate
          ? selectedEndDate
          : initialEndDate,
        employee_id,
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.details || []);
        setStats(resp.data.summary || {});
      } else {
        setCurrentItems([]);
        setStats({});
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      setCurrentItems([]);
      setStats({});

      swal("No results found...");
    }
  };

  // ---------------------------------------------------------
  // CSV EXPORT
  // ---------------------------------------------------------

  const handleExportCSV = async () => {
    const employee_id = selectedEmployee
      ? selectedEmployee
      : "0";

    const startDate = selectedStartDate
      ? selectedStartDate
      : initialStartDate;

    const endDate = selectedEndDate
      ? selectedEndDate
      : initialEndDate;

    try {
      setIsExporting(true);

      /*
       * Tax report endpoint.
       *
       * Change this URL if your backend uses a different
       * endpoint for exporting the tax report.
       */
      const response = await fetch(
        `http://127.0.0.1:8000/user/sales-by-tax-name/${user.id}/${startDate}/${endDate}/${employee_id}`
      );

      const result = await response.json();

      if (
        !result.success ||
        !result.data?.details?.length
      ) {
        swal(
          "No data",
          "No tax records found to export",
          "info"
        );

        setIsExporting(false);
        return;
      }

      const report = result.data.details;

      const headers = [
        "Tax Name",
        "Tax Rate",
        "Taxable Sales",
        "Tax Amount",
      ];

      const escapeCsvValue = (value) => {
        const stringValue = value ?? "";

        return `"${String(stringValue).replace(
          /"/g,
          '""'
        )}"`;
      };

      const rows = report.map((item) => [
        item.tax_name,
        item.tax_rate,
        item.taxable_sales,
        item.total_tax_amount,
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row.map(escapeCsvValue).join(",")
        )
        .join("\n");

      const blob = new Blob(
        [csvContent],
        {
          type: "text/csv;charset=utf-8;",
        }
      );

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `tax_report_${new Date()
        .toISOString()
        .split("T")[0]}.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      setIsExporting(false);
    } catch (error) {
      console.error(
        "Tax CSV Export Error:",
        error
      );

      swal(
        "Error",
        "Something went wrong while exporting data",
        "error"
      );

      setIsExporting(false);
    }
  };

  // ---------------------------------------------------------
  // DATE DISPLAY
  // ---------------------------------------------------------

  const displayedStartDate = date_range
    ? date_range.startDate
    : state[0].startDate;

  const displayedEndDate = date_range
    ? date_range.endDate
    : state[0].endDate;

  // ---------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      <div className="w-full px-3 sm:px-5 lg:px-6 pt-5">

        {/* =====================================================
            PAGE HEADER
        ====================================================== */}

        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 shrink-0">
            <ReceiptTaxIcon className="w-6 h-6 text-[#80b643]" />
          </div>

          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Tax Report
            </h1>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              View taxable sales, tax rates and tax amounts
            </p>
          </div>
        </div>

        {/* =====================================================
            FILTER CARD
        ====================================================== */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 mb-5">

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">

            {/* DATE RANGE */}

            <div
              ref={pickerRef}
              className="relative w-full lg:w-auto"
            >
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Report Period
              </label>

              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full lg:w-auto min-w-[250px] flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 px-3 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:border-[#80b643] focus:outline-none transition"
              >
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#80b643]" />

                  <span>
                    {format(
                      displayedStartDate,
                      "MMM dd, yyyy"
                    )}{" "}
                    →{" "}
                    {format(
                      displayedEndDate,
                      "MMM dd, yyyy"
                    )}
                  </span>
                </div>

                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{
                      opacity: 0,
                      y: -8,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      y: -8,
                    }}
                    transition={{
                      duration: 0.2,
                    }}
                    className="absolute z-50 mt-2 left-0 bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 rounded-xl p-2 overflow-hidden"
                  >
                    <DateRangePicker
                      onChange={handleSelect}
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      months={1}
                      ranges={state}
                      direction="horizontal"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* EMPLOYEE */}

            <div className="w-full lg:w-auto">
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Employee
              </label>

              <div className="relative min-w-[230px]">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#80b643] pointer-events-none" />

                <select
                  value={selectedEmployee}
                  onChange={(e) => {
                    setSelectedEmployee(
                      e.target.value
                    );

                    handleEmployeeChange(
                      e,
                      e.target.value
                    );
                  }}
                  className="appearance-none w-full bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 pl-9 pr-9 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:border-[#80b643] focus:outline-none focus:border-[#80b643] cursor-pointer transition"
                >
                  <option value="">
                    All Employees
                  </option>

                  {employees?.data?.data?.map(
                    (item) => (
                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.fname} {item.lname}
                      </option>
                    )
                  )}
                </select>

                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            SUMMARY CARDS
        ====================================================== */}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">

          {/* TAXABLE SALES */}

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />
              </div>

              <div className="min-w-0">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Taxable Sales
                </p>

                <p className="mt-1 text-xl sm:text-2xl font-semibold text-gray-700 truncate">
                  K{" "}
                  {formatMoney(
                    stats?.taxable_sales
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* NON TAXABLE SALES */}

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />
              </div>

              <div className="min-w-0">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Non-Taxable Sales
                </p>

                <p className="mt-1 text-xl sm:text-2xl font-semibold text-gray-700 truncate">
                  K{" "}
                  {formatMoney(
                    stats?.non_taxable_sales
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* TOTAL NET SALES */}

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />
              </div>

              <div className="min-w-0">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Net Sales
                </p>

                <p className="mt-1 text-xl sm:text-2xl font-semibold text-gray-700 truncate">
                  K{" "}
                  {formatMoney(
                    stats?.total_net_sales
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            REPORT TABLE CARD
        ====================================================== */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {/* TABLE HEADER */}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-4 border-b border-gray-200 dark:border-gray-800">

            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Tax Breakdown
              </h2>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Tax breakdown for the selected reporting period
              </p>
            </div>

            <button
              type="button"
              onClick={handleExportCSV}
              disabled={
                isExporting ||
                currentItems.length === 0
              }
              className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium shadow-sm transition ${
                isExporting ||
                currentItems.length === 0
                  ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-700 cursor-not-allowed"
                  : "bg-[#80b643] hover:bg-[#719f3a] text-white border-[#80b643]"
              }`}
            >
              <PrinterIcon className="w-4 h-4" />

              {isExporting
                ? "Exporting..."
                : "Export CSV"}
            </button>
          </div>

          {/* ===================================================
              TABLE
          ==================================================== */}

          {currentItems.length > 0 ? (
            <div className="overflow-x-auto">

              <table className="w-full min-w-[760px]">

                <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">
                  <tr>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Tax Name
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Tax Rate
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Taxable Sales
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Tax Amount
                    </th>

                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                  {currentItems.map(
                    (item, index) => {

                      const isTotal =
                        item.is_total;

                      return (
                        <tr
                          key={
                            item.employee_id ??
                            item.tax_id ??
                            index
                          }
                          className={`transition ${
                            isTotal
                              ? "bg-gray-50 dark:bg-gray-800/60 border-t-2 border-[#80b643]"
                              : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          }`}
                        >

                          {/* TAX NAME */}

                          <td className="px-5 py-4">

                            <div className="flex items-center gap-3">

                              <div
                                className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                                  isTotal
                                    ? "bg-[#80b643]/15 dark:bg-[#80b643]/20"
                                    : "bg-[#80b643]/10 dark:bg-[#80b643]/20"
                                }`}
                              >
                                <ReceiptTaxIcon className="w-4 h-4 text-[#80b643]" />
                              </div>

                              <div>
                                <p
                                  className={`text-sm ${
                                    isTotal
                                      ? "font-semibold text-gray-700"
                                      : "font-medium text-gray-900 dark:text-white"
                                  }`}
                                >
                                  {item.tax_name ||
                                    "Total"}
                                </p>
                                
                              </div>

                            </div>

                          </td>

                          {/* TAX RATE */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            {isTotal ? (
                              <span className="text-sm font-semibold text-gray-700">
                                —
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-gray-700 text-xs font-medium">
                                {item.tax_rate ?? 0}%
                              </span>
                            )}

                          </td>

                          {/* TAXABLE SALES */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span
                              className={`text-sm ${
                                isTotal
                                  ? "font-semibold text-gray-700"
                                  : "font-medium text-gray-700 dark:text-gray-200"
                              }`}
                            >
                              K{" "}
                              {formatMoney(
                                item.taxable_sales
                              )}
                            </span>

                          </td>

                          {/* TAX AMOUNT */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span
                              className={`text-sm ${
                                isTotal
                                  ? "font-semibold text-gray-600]"
                                  : "text-gray-700"
                              }`}
                            >
                              K{" "}
                              {formatMoney(
                                item.total_tax_amount
                              )}
                            </span>

                          </td>

                        </tr>
                      );
                    }
                  )}

                </tbody>

              </table>
            </div>
          ) : (
            /* =================================================
               EMPTY STATE
            ================================================== */

            <div className="flex flex-col items-center justify-center py-16 px-4">

              <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                <ReceiptTaxIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />
              </div>

              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                No tax records found
              </h3>

              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center max-w-sm">
                There are no tax records available for
                the selected date range or employee.
              </p>

            </div>
          )}

          {/* ===================================================
              TABLE FOOTER
          ==================================================== */}

          {currentItems.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {currentItems.length}
                </span>{" "}
                tax records
              </p>

              <p className="text-xs text-gray-400 dark:text-gray-500">
                {format(
                  displayedStartDate,
                  "MMM dd, yyyy"
                )}{" "}
                →{" "}
                {format(
                  displayedEndDate,
                  "MMM dd, yyyy"
                )}
              </p>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ManageTaxReportView;